import { Link, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import type { ICourse, IPage } from '@stem/contracts';
import { apiClient } from '../services/api';
import { useSession } from '../store/session';
import { EmptyState, Section, Stat } from './parts';

interface LabSummary {
  id: string;
  slug: string;
  title: string;
  status: string;
  version: number;
}

/**
 * The content developer's view.
 *
 * The distinction this screen exists to make visible: a `CONTENT_DEVELOPER` can
 * create and update courses, lessons and labs, and cannot publish any of them.
 * Drafting is private; publishing is what puts a lesson in front of thousands
 * of learners in a county, and it is a different decision made by a different
 * person. So the drafts list shows a "ready for review" state rather than a
 * publish button they would only be refused.
 */
export function ContentDeveloperDashboard() {
  const { orgSlug = '' } = useParams();
  const canPublish = useSession(
    state => state.permissions.includes('course@publish') || state.permissions.includes('*@*'),
  );

  const courses = useQuery({
    queryKey: ['my-courses'],
    queryFn: () => apiClient.get<IPage<ICourse>>('/v1/catalog/courses?sort=-updatedAt&limit=50'),
  });

  const labs = useQuery({
    queryKey: ['labs'],
    queryFn: () => apiClient.get<LabSummary[]>('/v1/labs'),
  });

  const rows = courses.data?.rows ?? [];
  const drafts = rows.filter(course => course.status !== 'published');
  const published = rows.filter(course => course.status === 'published');

  return (
    <div>
      <h1 className="text-2xl font-bold text-ink-900">Content</h1>
      <p className="text-sm text-ink-600">
        {canPublish
          ? 'Author, review and publish.'
          : 'Author and revise. Publishing is done by a reviewer.'}
      </p>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        <Stat label="Published courses" value={published.length} tone="good" />
        <Stat label="Drafts" value={drafts.length} />
        <Stat label="Laboratories" value={labs.data?.length ?? '—'} />
      </div>

      <Section title="Drafts" action={{ label: 'All courses', to: `/o/${orgSlug}/courses` }}>
        {drafts.length === 0 ? (
          <EmptyState>Nothing in draft.</EmptyState>
        ) : (
          <ul className="space-y-2">
            {drafts.map(course => (
              <li key={course.id} className="card flex flex-wrap items-center gap-3">
                <Link
                  to={`/o/${orgSlug}/courses/${course.slug}`}
                  className="min-w-0 flex-1 truncate text-sm font-medium text-ink-900 hover:underline"
                >
                  {course.title}
                </Link>
                <span className="badge bg-ink-200 text-ink-700">{course.status}</span>
                <span className="text-xs text-ink-500">
                  {new Date(course.updatedAt).toLocaleDateString()}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Section>

      <Section title="Laboratories" action={{ label: 'All labs', to: `/o/${orgSlug}/labs` }}>
        {labs.data && labs.data.length > 0 ? (
          <ul className="space-y-2">
            {labs.data.map(lab => (
              <li key={lab.id} className="card flex flex-wrap items-center gap-3">
                <span className="min-w-0 flex-1 truncate text-sm font-medium text-ink-900">
                  {lab.title}
                </span>
                <span className="text-xs text-ink-500">v{lab.version}</span>
                <span
                  className={
                    lab.status === 'published'
                      ? 'badge bg-emerald-100 text-emerald-800'
                      : 'badge bg-ink-200 text-ink-700'
                  }
                >
                  {lab.status}
                </span>
                <Link
                  to={`/o/${orgSlug}/labs/${lab.slug}/preview`}
                  className="text-xs text-brand-700 underline"
                >
                  Edit script
                </Link>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState>
            No laboratories yet. Install the <code>ke-cbc-g7-math</code> pack from{' '}
            <Link to={`/o/${orgSlug}/packs`} className="text-brand-700 underline">
              Curriculum packs
            </Link>{' '}
            to get a worked example to author against.
          </EmptyState>
        )}
      </Section>
    </div>
  );
}
